/**
 * System Health Report Page
 * Real-time overview of system performance and operational status
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableHead,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner } from '../../components/common';
import { SystemHealthService } from '../../services/reports/SystemHealthService';
import { formatDateTime } from '../../utils/dateUtils';
import type { SystemHealthData, KPICard, ReportFilters } from '../../types/reports';
import {
  Card,
  CardContent,
  TableCell,
  TableHeaderCell,
  TableRow,
  Select,
  SecondaryButton,
  DataTablePagination,
} from '../../components/ui';

type TabValue = 'api-matching' | 'failed-tasks' | 'notifications' | 'third-party';

const SystemHealthPage: React.FC = () => {
  const theme = useTheme();
  const [data, setData] = useState<SystemHealthData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabValue>('api-matching');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [filters, setFilters] = useState<ReportFilters>({
    dateRange: 'Last 7 Days',
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await SystemHealthService.getSystemHealthData(filters);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch system health data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatLastUpdated = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds} seconds ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} mins ago`;
    return `${Math.floor(seconds / 3600)} hours ago`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return theme.palette.success.main;
      case 'degraded':
        return theme.palette.warning.main;
      case 'outage':
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircleIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />;
      case 'degraded':
        return <WarningIcon sx={{ fontSize: 20, color: theme.palette.warning.main }} />;
      case 'outage':
        return <ErrorIcon sx={{ fontSize: 20, color: theme.palette.error.main }} />;
      default:
        return null;
    }
  };

  const getTrendIcon = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ fontSize: 14 }} />;
      case 'down':
        return <TrendingDownIcon sx={{ fontSize: 14 }} />;
      default:
        return <RemoveIcon sx={{ fontSize: 14 }} />;
    }
  };

  const getTrendColor = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return theme.palette.error.main;
      case 'down':
        return theme.palette.success.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const renderKPICard = (kpi: KPICard) => (
    <Card
      sx={{
        border: `1px solid ${theme.customColors.tableBorder}`,
        height: '100%',
      }}
    >
      <CardContent>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            marginBottom: theme.spacing(1),
            fontWeight: 500,
          }}
        >
          {kpi.title}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
          }}
        >
          {kpi.value}
        </Typography>
        {kpi.change && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Box sx={{ color: getTrendColor(kpi.trend) }}>
              {getTrendIcon(kpi.trend)}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: getTrendColor(kpi.trend),
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              {kpi.change}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderTableData = () => {
    if (!data) return null;

    switch (activeTab) {
      case 'api-matching':
        return (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>TIMESTAMP</TableHeaderCell>
                <TableHeaderCell>ERROR CODE</TableHeaderCell>
                <TableHeaderCell>COMPONENT</TableHeaderCell>
                <TableHeaderCell>DESCRIPTION</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.errorDetails.apiAndMatching
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((error, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                      {formatDateTime(error.timestamp)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {error.errorCode}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {error.component}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {error.description}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        );

      case 'failed-tasks':
        return (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>TIMESTAMP</TableHeaderCell>
                <TableHeaderCell>TASK ID</TableHeaderCell>
                <TableHeaderCell>REASON</TableHeaderCell>
                <TableHeaderCell>COMPONENT</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.errorDetails.failedTaskAllocations
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((task, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                      {formatDateTime(task.timestamp)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {task.taskId}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {task.reason}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {task.component}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        );

      case 'notifications':
        return (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>TIMESTAMP</TableHeaderCell>
                <TableHeaderCell>TYPE</TableHeaderCell>
                <TableHeaderCell>STATUS</TableHeaderCell>
                <TableHeaderCell>RECIPIENT</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.errorDetails.notificationLogs
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((log, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                      {formatDateTime(log.timestamp)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {log.type}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.status}
                        size="small"
                        sx={{
                          backgroundColor:
                            log.status === 'Sent'
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                          color: theme.palette.common.white,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {log.recipient}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        );

      case 'third-party':
        return (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>TIMESTAMP</TableHeaderCell>
                <TableHeaderCell>SERVICE</TableHeaderCell>
                <TableHeaderCell>ERROR CODE</TableHeaderCell>
                <TableHeaderCell>DESCRIPTION</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.errorDetails.thirdPartyIntegrations
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((integration, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                      {formatDateTime(integration.timestamp)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {integration.service}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {integration.errorCode}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {integration.description}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        );

      default:
        return null;
    }
  };

  const getCurrentTabDataLength = () => {
    if (!data) return 0;
    switch (activeTab) {
      case 'api-matching':
        return data.errorDetails.apiAndMatching.length;
      case 'failed-tasks':
        return data.errorDetails.failedTaskAllocations.length;
      case 'notifications':
        return data.errorDetails.notificationLogs.length;
      case 'third-party':
        return data.errorDetails.thirdPartyIntegrations.length;
      default:
        return 0;
    }
  };

  if (isLoading && !data) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No data available.
        </Typography>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: theme.spacing(2) }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(0.5),
              }}
            >
              System Health Report
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Real-time overview of system performance and operational status.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.75rem',
              }}
            >
              Last updated: {formatLastUpdated(data.lastUpdated)}
            </Typography>
            <SecondaryButton
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
            >
              Refresh
            </SecondaryButton>
          </Box>
        </Box>

        {/* Filters */}
        <Box
          sx={{
            display: 'flex',
            gap: theme.spacing(2),
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Select
            size="small"
            label="Date Range"
            value={filters.dateRange || 'Last 7 Days'}
            onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as string })}
            formControlProps={{ sx: { minWidth: 150 } }}
          >
            <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
            <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
            <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
            <MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
          </Select>

          <Select
            size="small"
            label="Component"
            value="All"
            formControlProps={{ sx: { minWidth: 150 } }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="API">API</MenuItem>
            <MenuItem value="Database">Database</MenuItem>
            <MenuItem value="Matching Engine">Matching Engine</MenuItem>
            <MenuItem value="Notification Service">Notification Service</MenuItem>
          </Select>

          <Select
            size="small"
            label="Severity"
            value="All"
            formControlProps={{ sx: { minWidth: 150 } }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Critical">Critical</MenuItem>
            <MenuItem value="Warning">Warning</MenuItem>
            <MenuItem value="Info">Info</MenuItem>
          </Select>
        </Box>
      </Box>

      {/* System Status Card */}
      <Grid container spacing={3} sx={{ marginBottom: theme.spacing(4) }}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 200,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2), marginBottom: theme.spacing(2) }}>
                {getStatusIcon(data.systemStatus.overall)}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    fontSize: '1rem',
                  }}
                >
                  Overall System Status: {data.systemStatus.overall === 'operational' ? 'All Systems Operational' : data.systemStatus.overall === 'degraded' ? 'System Degraded' : 'System Outage'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
                {data.systemStatus.components.map((component, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                    {getStatusIcon(component.status)}
                    <Typography
                      variant="body2"
                      sx={{
                        color: getStatusColor(component.status),
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                    >
                      {component.name}: {component.status.charAt(0).toUpperCase() + component.status.slice(1)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Key Metrics */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={4}>
              {renderKPICard(data.keyMetrics.dailyApiErrors)}
            </Grid>
            <Grid item xs={6} sm={4} md={4}>
              {renderKPICard(data.keyMetrics.matchingEngineErrors)}
            </Grid>
            <Grid item xs={6} sm={4} md={4}>
              {renderKPICard(data.keyMetrics.failedTasks)}
            </Grid>
            <Grid item xs={6} sm={4} md={4}>
              {renderKPICard(data.keyMetrics.notificationSuccess)}
            </Grid>
            <Grid item xs={6} sm={4} md={4}>
              {renderKPICard(data.keyMetrics.thirdPartyErrors)}
            </Grid>
            <Grid item xs={6} sm={4} md={4}>
              {renderKPICard(data.keyMetrics.avgApiResponse)}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ marginBottom: theme.spacing(4) }}>
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: '1rem',
                }}
              >
                Error Trend (Last 7 Days)
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={data.errorTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                  <XAxis
                    dataKey="date"
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="apiErrors"
                    stroke={theme.palette.error.main}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="API Errors"
                  />
                  <Line
                    type="monotone"
                    dataKey="matchingErrors"
                    stroke={theme.palette.warning.main}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Matching Errors"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              minHeight: 400,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: '1rem',
                }}
              >
                Errors by Type
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={data.errorsByType}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ type, percent }: any) => `${type}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {data.errorsByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Details Table */}
      <Card
        sx={{
          border: `1px solid ${theme.customColors.tableBorder}`,
        }}
      >
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: theme.customColors.tableBorder }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => {
                setActiveTab(newValue as TabValue);
                setPage(0);
              }}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minHeight: 48,
                },
                '& .Mui-selected': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              <Tab label="API & Matching Errors" value="api-matching" />
              <Tab label="Failed Task Allocations" value="failed-tasks" />
              <Tab label="Notification Logs" value="notifications" />
              <Tab label="3rd-Party Integrations" value="third-party" />
            </Tabs>
          </Box>
          <Box sx={{ marginTop: theme.spacing(2) }}>
            {renderTableData()}
          </Box>
          <DataTablePagination
            totalPages={Math.ceil(getCurrentTabDataLength() / rowsPerPage)}
            page={page + 1}
            onPageChange={(_, newPage) => setPage(newPage - 1)}
            showStats
            startIndex={page * rowsPerPage + 1}
            endIndex={Math.min((page + 1) * rowsPerPage, getCurrentTabDataLength())}
            totalResults={getCurrentTabDataLength()}
            layout="space-between"
          />
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default SystemHealthPage;

